import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Loader2, Lock, ArrowRight, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { findVariant, productUrl, shipping, returns, products, findProduct } from '../../content/products';
import { fmt, toCents } from '../../lib/shopPricing';
import { createCheckout, track, API_URL } from '../../lib/shopApi';
import { useCart } from '../../context/CartContext';
import InstallPicker from '../../components/shop/InstallPicker';
import { Breadcrumbs } from '../../components/shop/ShopBits';
import usePageMeta from '../../hooks/usePageMeta';

const Field = ({ id, label, ...rest }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} {...rest} />
  </div>
);

const CartPage = () => {
  usePageMeta();
  const { cart, quote, addItem, updateQty, removeItem, setInstall, setShippingChoice, setPayment, setContact } = useCart();
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const c = cart.contact || {};

  useEffect(() => {
    if (cart.items.length) track('view_cart', { items: cart.items.length });
  }, [cart.items.length]);

  const firstEligible = useMemo(() => {
    for (const line of cart.items) {
      const hit = findVariant(line.variantId);
      if (hit && hit.product.install.eligible) return hit.product;
    }
    return null;
  }, [cart.items]);

  const installOn = !!(cart.install && cart.install.selected && firstEligible);
  const requiredOk = c.name && c.email && c.phone && c.address1 && c.city && c.postcode && (!installOn || (cart.install && cart.install.suburb));
  // Door photos are asked for after payment (success page + confirmation email), never here
  // (Hugh, 17 Sep 2026): the customer pays first, the clarifying comes after.
  // A different type of lock leaves the old holes; offer the cover plates now, not on the day.
  const coverPlates = findProduct('e-lok-cover-plates');
  const suggestCover = installOn && cart.install && cart.install.existingLock === 'different' && coverPlates
    && !cart.items.some((l) => coverPlates.variants.some((v) => v.id === l.variantId));

  const checkout = async () => {
    setError('');
    if (!quote || quote.error) {
      setError((quote && quote.error) || 'Your cart is empty.');
      return;
    }
    if (!requiredOk) {
      setError(installOn ? 'Fill in your details and pick the install suburb.' : 'Fill in your name, email, phone and delivery address.');
      return;
    }
    if (!agree) {
      setError('Please confirm you have read the shop terms, shipping & returns and warranty.');
      return;
    }
    setBusy(true);
    try {
      track('begin_checkout', { total: quote.totalCents / 100, payment: cart.payment, install: installOn });
      const res = await createCheckout({
        items: cart.items.map((l) => ({ variantId: l.variantId, qty: l.qty, backsetMm: l.backsetMm, backsetMeasuredMm: l.backsetMeasuredMm || null, noMortice: !!l.noMortice })),
        install: installOn ? {
          selected: true,
          doorMaterial: cart.install.doorMaterial,
          existingLock: cart.install.existingLock,
          existingLockType: cart.install.existingLockType || null,
          sliding: cart.install.sliding,
          suburb: cart.install.suburb,
          hubSetup: !!cart.install.hubSetup,
          doorNotes: cart.install.doorNotes || null,
          preferredTimes: cart.install.preferredTimes || null,
        } : null,
        shippingChoice: cart.shippingChoice,
        payment: cart.payment,
        contact: c,
        expectedTotalCents: quote.totalCents,
      });
      if (res && res.url) {
        window.location.assign(res.url);
        return;
      }
      throw new Error('No payment page was returned.');
    } catch (err) {
      setError(err.message || 'Checkout is unavailable right now — please call us.');
      setBusy(false);
    }
  };

  if (!cart.items.length) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Your cart is empty</h1>
          <p className="text-gray-700 mb-6">Start with the lock that fits your door.</p>
          <Link to="/shop">
            <Button className="bg-red-600 hover:bg-red-700 text-white font-bold">
              Back to the shop <ArrowRight className="ml-2" size={16} />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs crumbs={[{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }, { name: 'Cart' }]} />
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">Your cart</h1>
        <div className="grid lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2 space-y-8">
            {/* Items */}
            <ul className="divide-y divide-gray-200 border border-gray-200 rounded-xl" data-testid="cart-items">
              {cart.items.map((line) => {
                const hit = findVariant(line.variantId);
                if (!hit) return null;
                const { product, variant } = hit;
                return (
                  <li key={line.lineId} className="p-4 flex gap-4 items-start">
                    <img src={product.images[0].sm || product.images[0].src} alt="" width={80} height={80} className="w-20 h-20 object-contain bg-white border border-gray-100 rounded-md flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <Link to={productUrl(product)} className="font-semibold text-gray-900 hover:text-blue-700">
                        {product.name}
                      </Link>
                      <p className="text-sm text-gray-600">
                        {variant.label}
                        {line.backsetMm ? ` · ${product.fit.sliding ? 'mortice' : 'backset'} ${line.backsetMm} mm` : ''}
                        {line.noMortice ? ' · leverset only, no mortice' : product.mortice ? ' · mortice included' : ''}
                      </p>
                      <div className="mt-2 flex items-center gap-3">
                        <label className="text-sm text-gray-700">
                          Qty{' '}
                          <select value={line.qty} onChange={(e) => updateQty(line.lineId, Number(e.target.value))} className="ml-1 rounded-md border border-gray-300 px-2 py-1 bg-white">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button type="button" onClick={() => removeItem(line.lineId)} className="text-sm text-gray-500 hover:text-red-600 inline-flex items-center gap-1" aria-label={`Remove ${product.shortName}`}>
                          <Trash2 size={14} /> Remove
                        </button>
                      </div>
                    </div>
                    <p className="font-semibold text-gray-900 whitespace-nowrap">{fmt(Math.round(product.priceIncGst * 100) * line.qty)}</p>
                  </li>
                );
              })}
            </ul>

            {/* Install */}
            {firstEligible && (
              <section className="rounded-xl border border-gray-200 p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={installOn} onChange={(e) => setInstall(e.target.checked ? { ...(cart.install || {}), selected: true } : null)} className="mt-1 h-4 w-4" data-testid="cart-install-toggle" />
                  <span>
                    <span className="font-semibold text-gray-900 inline-flex items-center gap-2">
                      <Wrench size={16} className="text-red-600" /> Add supply &amp; install in Auckland
                    </span>
                    <span className="block text-sm text-gray-600">Fitted price on screen · no courier, the lock comes with the installer.</span>
                  </span>
                </label>
                {installOn && (
                  <div className="mt-4">
                    <InstallPicker product={firstEligible} value={cart.install} onChange={(v) => setInstall({ ...v, selected: true })} count={quote && quote.install ? quote.install.count : 1} smart={cart.items.some((l) => { const p = products.find((x) => x.variants.some((vv) => vv.id === l.variantId)); return !!(p && p.ecosystems && p.ecosystems.length); })} />
                    {suggestCover && (
                      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="cart-cover-plates">
                        <span className="text-sm text-gray-800"><span className="font-semibold">A different lock type leaves old holes.</span> Cover plates ({fmt(toCents(coverPlates.priceIncGst))}) hide them; sized before we book.</span>
                        <button type="button" onClick={() => addItem(coverPlates.variants[0].id, { product: coverPlates })} className="rounded-full bg-gray-900 text-white text-sm font-semibold px-4 py-2 hover:bg-gray-700">Add cover plates</button>
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {/* Shipping */}
            {!installOn && (
              <section className="rounded-xl border border-gray-200 p-5">
                <h2 className="font-semibold text-gray-900 mb-3">Delivery</h2>
                <p className="text-sm text-gray-600 mb-3">
                  {shipping.courierLine} · added to every shipped order.
                </p>
                <div className="flex flex-wrap gap-3">
                  {[
                    ['standard', `Courier ${fmt(Math.round(shipping.standard * 100))}`],
                    ['rural', `Rural delivery ${fmt(Math.round(shipping.rural * 100))}`],
                  ].map(([k, label]) => (
                    <label key={k} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${cart.shippingChoice === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-300 text-gray-700'}`}>
                      <input type="radio" name="shipping" value={k} checked={cart.shippingChoice === k} onChange={() => setShippingChoice(k)} className="sr-only" />
                      {label}
                    </label>
                  ))}
                </div>
              </section>
            )}

            {/* Contact + address */}
            <section className="rounded-xl border border-gray-200 p-5">
              <h2 className="font-semibold text-gray-900 mb-3">{installOn ? 'Your details and the site address' : 'Your details and delivery address'}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="c-name" label="Name" value={c.name || ''} onChange={(e) => setContact({ name: e.target.value })} required autoComplete="name" />
                <Field id="c-email" label="Email" type="email" value={c.email || ''} onChange={(e) => setContact({ email: e.target.value })} required autoComplete="email" />
                <Field id="c-phone" label="Mobile" value={c.phone || ''} onChange={(e) => setContact({ phone: e.target.value })} required autoComplete="tel" />
                <Field id="c-address1" label="Street address" value={c.address1 || ''} onChange={(e) => setContact({ address1: e.target.value })} required autoComplete="address-line1" />
                <Field id="c-address2" label="Unit / building (optional)" value={c.address2 || ''} onChange={(e) => setContact({ address2: e.target.value })} autoComplete="address-line2" />
                <Field id="c-suburb" label="Suburb" value={c.suburb || ''} onChange={(e) => setContact({ suburb: e.target.value })} autoComplete="address-level3" />
                <Field id="c-city" label="City / town" value={c.city || ''} onChange={(e) => setContact({ city: e.target.value })} required autoComplete="address-level2" />
                <Field id="c-postcode" label="Postcode" value={c.postcode || ''} onChange={(e) => setContact({ postcode: e.target.value })} required autoComplete="postal-code" inputMode="numeric" />
              </div>
              <div className="mt-4">
                <Label htmlFor="c-notes">Notes for us (optional)</Label>
                <Input id="c-notes" value={c.notes || ''} onChange={(e) => setContact({ notes: e.target.value })} placeholder="Gate code, best time to call, anything about the door" />
              </div>
            </section>

            {/* Payment */}
            <section className="rounded-xl border border-gray-200 p-5">
              <h2 className="font-semibold text-gray-900 mb-3">How would you like to pay?</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  ['card', 'Card, Apple Pay, Google Pay or Afterpay', 'Secure payment page by Stripe.'],
                  ['poli', 'Pay from your bank (POLi)', 'Log in to your NZ internet banking — no card needed.'],
                ].map(([k, label, sub]) => (
                  <label key={k} className={`rounded-lg border p-4 cursor-pointer ${cart.payment === k ? 'border-gray-900 bg-gray-50' : 'border-gray-300'}`}>
                    <input type="radio" name="payment" value={k} checked={cart.payment === k} onChange={() => setPayment(k)} className="sr-only" />
                    <span className="font-semibold text-gray-900 block">{label}</span>
                    <span className="text-sm text-gray-600">{sub}</span>
                  </label>
                ))}
              </div>
            </section>
          </div>

          {/* Summary */}
          <aside className="rounded-2xl border border-gray-200 p-6 lg:sticky lg:top-40" data-testid="order-summary">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Order summary</h2>
            {quote && !quote.error ? (
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-600">Items</dt>
                  <dd className="text-gray-900">{fmt(quote.itemsCents)}</dd>
                </div>
                {quote.install && (
                  <div className="flex justify-between">
                    <dt className="text-gray-600">
                      Installation — {quote.install.tierLabel}
                      {quote.install.count > 1 ? ` × ${quote.install.count}` : ''}
                    </dt>
                    <dd className="text-gray-900">{fmt(quote.install.cents)}</dd>
                  </div>
                )}
                {quote.install && quote.install.hubSetupCents > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Smart hub &amp; app setup</dt>
                    <dd className="text-gray-900">{fmt(quote.install.hubSetupCents)}</dd>
                  </div>
                )}
                {quote.install && quote.install.travelCents > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Travel ({quote.install.zone === 'outer' ? 'wider Auckland' : 'to be confirmed'})</dt>
                    <dd className="text-gray-900">{fmt(quote.install.travelCents)}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-gray-600">{quote.shippingLabel}</dt>
                  <dd className="text-gray-900">{quote.shippingCents ? fmt(quote.shippingCents) : '—'}</dd>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-3 text-base">
                  <dt className="font-bold text-gray-900">Total incl. GST</dt>
                  <dd className="font-bold text-gray-900" data-testid="cart-total">
                    {fmt(quote.totalCents)}
                  </dd>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <dt>Includes GST of</dt>
                  <dd>{fmt(quote.gstCents)}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-sm text-amber-700">{quote ? quote.error : 'Add an item to see the total.'}</p>
            )}

            <label className="flex items-start gap-2 mt-5 text-xs text-gray-700 cursor-pointer">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" data-testid="agree-terms" />
              <span>
                I have read the{' '}
                <Link to="/shop/terms" className="underline">
                  shop terms
                </Link>
                ,{' '}
                <Link to="/shop/shipping-returns" className="underline">
                  shipping &amp; returns
                </Link>{' '}
                and{' '}
                <Link to="/shop/warranty" className="underline">
                  warranty
                </Link>
                .
              </span>
            </label>

            {error && (
              <p className="mt-3 text-sm text-red-700" role="alert" data-testid="checkout-error">
                {error}
              </p>
            )}
            <Button onClick={checkout} disabled={busy || !quote || !!quote.error} className="w-full mt-4 bg-red-600 hover:bg-red-700 text-white font-bold py-3" data-testid="checkout-button">
              {busy ? <Loader2 className="mr-2 animate-spin" size={18} /> : <Lock className="mr-2" size={18} />}
              {cart.payment === 'poli' ? 'Pay with POLi' : 'Pay securely'}
            </Button>
            {!API_URL && <p className="mt-2 text-xs text-amber-700">Checkout is not connected in this build (REACT_APP_BACKEND_URL unset).</p>}
            <p className="mt-3 text-xs text-gray-500">Prices include GST. A GST receipt is emailed with your confirmation.</p>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
